import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Users, Search } from 'lucide-react';
import api from '../../lib/api';
import { kolUrlSegment } from '../../lib/slug';
import { kolTotalFollowers } from '../../lib/kolFollowers';
import type { KOLProfile } from '../../types';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import DataTable from '../../components/ui/DataTable';

type KOL = Pick<
  KOLProfile,
  | 'id'
  | 'name'
  | 'urlSlug'
  | 'platforms'
  | 'instagramFollowers'
  | 'tiktokFollowers'
  | 'youtubeFollowers'
  | 'facebookFollowers'
  | 'wechatFollowers'
> & { tags: any[]; audience: any };

export default function CorporateKOLs() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const kolsQuery = useQuery({
    queryKey: ['brand', 'kols', 'brand-visible'],
    queryFn: () => api.get<KOL[]>('/kols/brand-visible').then((r) => r.data),
    staleTime: 60 * 1000,
  });

  const kols = kolsQuery.data ?? [];

  const filteredKOLs = useMemo(
    () => kols.filter((kol) => kol.name.toLowerCase().includes(searchTerm.toLowerCase())),
    [kols, searchTerm]
  );

  const getPlatformIcons = (platforms: any) => {
    if (!platforms) return null;
    const icons = [];
    if (platforms.instagram) icons.push('📷');
    if (platforms.tiktok) icons.push('🎵');
    if (platforms.youtube) icons.push('▶️');
    if (platforms.facebook) icons.push('👥');
    return icons.join(' ');
  };

  const columns = [
    {
      header: 'Name',
      accessor: (row: KOL) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold">
            {row.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="font-medium text-glass-text">{row.name}</div>
            <div className="text-sm text-glass-text-3">{getPlatformIcons(row.platforms)}</div>
          </div>
        </div>
      ),
    },
    {
      header: 'Total Reach',
      accessor: (row: KOL) => kolTotalFollowers(row as KOLProfile).toLocaleString(),
    },
    {
      header: 'Tags',
      accessor: (row: KOL) => (
        <div className="flex flex-wrap gap-1">
          {row.tags?.slice(0, 3).map((tagRel: any) => (
            <Badge key={tagRel.tag.id} variant="secondary" className="text-xs">
              {tagRel.tag.name}
            </Badge>
          ))}
          {row.tags && row.tags.length > 3 && (
            <Badge variant="outline" className="text-xs">
              +{row.tags.length - 3}
            </Badge>
          )}
        </div>
      ),
    },
    {
      header: 'Demographics',
      accessor: (row: KOL) => {
        const audience = row.audience;
        if (!audience) return 'N/A';
        const ageGroups = Object.entries(audience.ageGroups || {})
          .sort((a: any, b: any) => b[1] - a[1])
          .slice(0, 2)
          .map(([group]) => group)
          .join(', ');
        return ageGroups || 'N/A';
      },
    },
  ];

  return (
    <div className="space-y-6 text-glass-text">
      <div>
        <h1 className="page-title">KOL Talent Pool</h1>
        <p className="text-glass-text-2 text-sm mt-1">
          View KOLs who have been shortlisted or approved for your jobs
        </p>
      </div>

      <div className="glass-card p-4">
        <div className="flex items-center gap-4">
          <Search className="w-5 h-5 text-glass-text-3" />
          <Input
            type="text"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1"
          />
        </div>
      </div>

      <div className="glass-card p-4 border-accent2/30 bg-accent2/10">
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 text-accent2 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-glass-text">
            <strong>Limited Visibility:</strong> You only see KOLs who have been shortlisted or approved for your
            jobs. For full KOL database access, please contact your agency partner.
          </div>
        </div>
      </div>

      <div className="glass-card overflow-hidden">
        <DataTable
          data={filteredKOLs}
          columns={columns}
          onRowClick={(kol) => navigate(`/brand/kols/${kolUrlSegment(kol)}`)}
          isLoading={kolsQuery.isPending}
          emptyMessage="No KOLs to display. KOLs will appear here once they are shortlisted or approved for your jobs."
        />
      </div>
    </div>
  );
}

